/**
 * Retainage comment text generation.
 *
 * Kept free of component imports so the wording can be unit-tested directly — the section that
 * renders it pulls in MUI and react-dnd, which the test environment can't load.
 */

/** The standard retainage rate; anything below it needs to be called out to the Lender. */
export const STANDARD_RETAINAGE_PERCENTAGE = 10;

export const STATUTORY_RIGHTS_SENTENCE =
  'We leave to the Lender and their legal counsel the determination of whether the Borrower and Lender are foregoing any of their statutory rights by not holding the full and statutory 10%.';

/** One covered draw (G702) and the retainage rate held on it. */
export interface DrawRetainage {
  drawId: string;
  name: string;
  percentage: number;
}

/** "A" / "A and B" / "A, B, and C" */
const joinWithAnd = (parts: string[]): string => {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
};

/**
 * Retainage statement for a report covering several G702s, which is judged per application rather
 * than on one blended rate:
 *
 * - every application at or above 10% -> the standard statement
 * - some below 10% -> the standard statement with those applications called out as exceptions
 * - every application below 10% -> each application's rate listed
 *
 * `includeStatutorySentence` is false when the caller appends its own closing sentences (the
 * Release Retainage Language flow).
 */
export const buildMultiDrawMainComment = (drawRetainages: DrawRetainage[], includeStatutorySentence: boolean): string => {
  const below = drawRetainages.filter(d => d.percentage < STANDARD_RETAINAGE_PERCENTAGE);
  const withStatutory = (text: string) => (includeStatutorySentence ? `${text} ${STATUTORY_RIGHTS_SENTENCE}` : text);

  if (below.length === 0) {
    return 'Retainage is currently being held at 10% of the Total Completed and Stored to date amounts.';
  }

  if (below.length === drawRetainages.length) {
    const parts = below.map(d => `${d.percentage.toFixed(2)}% for the ${d.name} application`);
    return withStatutory(
      `Note that Retainage is not being held at the standard 10% of the Total Completed and Stored to date amount, but is instead being held at ${joinWithAnd(
        parts,
      )}.`,
    );
  }

  // Mixed: hold the standard statement and name the exceptions.
  const isSingleException = below.length === 1;
  const names = joinWithAnd(below.map(d => d.name));
  const applicationWord = isSingleException ? 'application' : 'applications';
  const subject = isSingleException ? 'this application' : 'these applications';
  const rates = isSingleException
    ? `${below[0].percentage.toFixed(2)}%`
    : `${joinWithAnd(below.map(d => `${d.percentage.toFixed(2)}%`))} respectively`;

  return withStatutory(
    `Retainage is currently being held at 10% of the Total Completed and Stored to date amounts except for the ${names} ${applicationWord}. Retainage is being held on ${subject} at ${rates}.`,
  );
};
